import { useCallback, useRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { getRowColumnCounts } from '@pkgs/components/utils/gridColumns';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { type TFunction } from '@pkgs/components/providers';
import { useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';
import { useListings } from '@pkgs/components/hooks/queries/useListings';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { AddressTypeDto, getSearchListingResultId, type AddressComponentDto, type SearchListingResult } from '@ev-tech/search-modules-api';

import { SearchResultCardWrapper } from '../SearchResults/SearchResultCardWrapper';

import styles from './WiderAreaResults.module.css';
import searchResultsStyles from '../SearchResults/SearchResults.module.css';

const widerAreaResultsVariants = cva(styles.container, {
  variants: {
    isMapView: {
      true: styles.mapView,
      false: null,
    },
  },
});

type WiderAreaResultsProps = {
  isMapView: NonNullable<VariantProps<typeof widerAreaResultsVariants>['isMapView']>;
};

export const getHeadlineText = ({ t, placeName }: { t: TFunction; placeName?: string }) => {
  if (placeName) {
    return t('search.results.widerAreaResults.label.headlineWithGeo', {
      location: placeName,
    });
  }
  return t('search.results.widerAreaResults.label.headline');
};

const LOCATION_TEXT_TYPE_PRIORITY = [AddressTypeDto.neighborhood, AddressTypeDto.sublocality_level_1, AddressTypeDto.sublocality];

export const extractLocationText = (addressComponents?: AddressComponentDto[], placeName?: string): string | undefined => {
  if (!addressComponents || addressComponents.length === 0) return placeName;

  const firstComponent = addressComponents[0];
  const lastComponent = addressComponents[addressComponents.length - 1];

  const postalCodeComponent = firstComponent?.types.includes(AddressTypeDto.postal_code) ? firstComponent : undefined;
  const countryComponent = lastComponent?.types.includes(AddressTypeDto.country) ? lastComponent : undefined;

  if (postalCodeComponent && countryComponent) {
    return `${postalCodeComponent.longName}, ${countryComponent.longName}`;
  }

  for (const type of LOCATION_TEXT_TYPE_PRIORITY) {
    const matchingComponent = addressComponents.find((comp) => comp.types.includes(type));
    if (matchingComponent) return matchingComponent.longName;
  }

  return placeName;
};

export const WiderAreaResults = ({ isMapView }: WiderAreaResultsProps) => {
  const { t } = useAppContext();
  const { data: results } = useListings();
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const { trackSearchResultCardClick } = useSearchModuleTracking();
  const isMapActive = useMapVisibilityStore((state) => state.isMapActive);
  const placeName = useSearchStore((state) => state.placeName);
  const { primaryPlaceDetails } = usePlaceDetails();

  const handleCardClick = useCallback(
    (exposeUrl: string, listingResult: SearchListingResult, index: number) => {
      const result = getRowColumnCounts(gridContainerRef, index);
      if (!result) return;
      const { row, column } = result;
      trackSearchResultCardClick({
        exposeUrl,
        listingResult,
        elementId: `surrounding_property-card_row-${row}_card-${column}`,
        isMapActive,
      });
    },
    [trackSearchResultCardClick, isMapActive],
  );

  if (results.widerAreaListings.length === 0) return null;

  return (
    <section className={widerAreaResultsVariants({ isMapView })}>
      <div className={styles.headlineContainer}>
        <Headline
          as="h2"
          size={isMapView ? 'h5' : 'h4'}
          label={getHeadlineText({
            t: t as TFunction,
            placeName: extractLocationText(primaryPlaceDetails?.addressComponents, placeName),
          })}
        />
      </div>

      <div ref={gridContainerRef} className={searchResultsStyles.resultsListContainer} data-map-view={isMapView}>
        {results.widerAreaListings.map((result, index) => (
          <SearchResultCardWrapper
            key={getSearchListingResultId(result)}
            shouldPreloadImage={false}
            listingResult={result}
            onClick={(exposeUrl) => handleCardClick(exposeUrl, result, index)}
          />
        ))}
      </div>
    </section>
  );
};
